/**
 * A calendar days strip's span layers and each one's last render context:
 * what a drag ghost or draft change repaints from synchronously, with no
 * re-`makeSpans` or fetch (as the grid widget's `renderGrid` reads
 * `props.options.spans`).
 *
 * `SpanContexts` is the map's one writer: a paint records a layer's context,
 * a failed read forgets it, a drop settles it, a recycle clears them all, and
 * live iteration prunes layers that left the DOM, each through a method here.
 * Everyone else reads `view`.
 *
 * Beside each context it keeps the certified read the layer was painted
 * under (the bind's `paintKey`, calendar-days-strip-bind.ts): a later read of
 * the same layer under the same key would paint the same spans, so the span
 * paint skips it (`paintedUnder`). Any other write of the context drops the key.
 */

import type { Option } from "@tento-lona/sheets";
import type { CalendarDaysStrip } from "./calendar-days-strip.ts";

/** A strip's span layers and their last render contexts (the one writer of that map). */
export class SpanContexts {
  #byLayer = new Map<HTMLElement, CalendarDaysStrip.SpanRenderCtx>();
  /** The certified read each layer's context was painted under (absent: none). */
  readonly #keys = new Map<HTMLElement, object>();

  /** The live map, read-only: what the ghost projection and the harness read. */
  get view(): ReadonlyMap<HTMLElement, CalendarDaysStrip.SpanRenderCtx> {
    return this.#byLayer;
  }

  /** `$layout`'s last render context, if it has one. */
  get($layout: HTMLElement): Option<CalendarDaysStrip.SpanRenderCtx> {
    return this.#byLayer.get($layout) ?? null;
  }

  /**
   * Record `ctx` as `$layout`'s render context (a paint, or a drop's settled
   * spans), painted under the certified read `key` (null: none, so no later
   * read may skip its paint).
   */
  set($layout: HTMLElement, ctx: CalendarDaysStrip.SpanRenderCtx, key: Option<object>): void {
    this.#byLayer.set($layout, ctx);
    if (key == null) this.#keys.delete($layout);
    else this.#keys.set($layout, key);
  }

  /** The certified read `$layout`'s context was painted under, or null. */
  paintedUnder($layout: HTMLElement): Option<object> {
    return this.#keys.get($layout) ?? null;
  }

  /** Forget `$layout` (its read failed on another date's cards). */
  delete($layout: HTMLElement): void {
    this.#byLayer.delete($layout);
    this.#keys.delete($layout);
  }

  /** Forget every layer (a recycle: nothing left for a draft change or drag to repaint). */
  clear(): void {
    this.#byLayer.clear();
    this.#keys.clear();
  }

  /** Run `fn` for each live span layer, pruning any whose `$layout` left the DOM (a recycled column). */
  forEachLive(fn: ($layout: HTMLElement, ctx: CalendarDaysStrip.SpanRenderCtx) => void): void {
    for (const [$layout, ctx] of this.#byLayer) {
      if (!$layout.isConnected) {
        this.#byLayer.delete($layout);
        this.#keys.delete($layout);
        continue;
      }
      fn($layout, ctx);
    }
  }

  /** Swap in a copy of `next` (the harness's `replaceSpanContexts`, no keys); returns the map it replaced. */
  replace(
    next: ReadonlyMap<HTMLElement, CalendarDaysStrip.SpanRenderCtx>,
  ): Map<HTMLElement, CalendarDaysStrip.SpanRenderCtx> {
    const previous = this.#byLayer;
    this.#byLayer = new Map(next);
    this.#keys.clear();
    return previous;
  }
}
